import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  Divider,
  Typography,
  useScrollTrigger,
} from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  ActionButton,
  ActionLink,
  Header,
  Icon,
  IconName,
  PackageHotelPriceAndRewardsDisplay,
  useDeviceTypes,
} from "halifax";
import React, { useContext, useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { RouteComponentProps } from "react-router-dom";
import queryStringParser from "query-string";
import {
  CallState,
  CorePackagesCheckout,
  CorePackagesCheckoutV2,
  PackagePricing,
  REDIRECT_TO_PACKAGE_HOTEL_SHOP,
  VIEWED_ITINERARY_REVIEW,
} from "redmond";
import {
  useIsTravelApp,
  isVesperTenant,
  getConfigTenant,
} from "@capone/common";
import { AVAILABLE, getExperimentVariant } from "@capone/experiments";
import { trackEvent } from "../../api/v0/trackEvent";
import { ClientContext } from "../../App";
import { PATH_HOME } from "../../utils/paths";
import { PackagesShopProgressBar } from "../book/components";
import { PackageDiscountBanner } from "../book/components/PackageDiscountBanner";
import {
  goToBook,
  goToHotelShop,
} from "../hotel-shop/utils/queryStringHelpers";
import { RewardsAccountSelection } from "../rewards/components";
import { ANCILLARY_DISCLAIMER_TEXT } from "../search/components/PackagesInfoModal/textConstants";
import { TravelWalletDrawer } from "../travel-wallet/components";
import { ChangeItineraryModal } from "./components/ChangeItineraryModal";
import { ReviewErrorModal } from "./components/ReviewErrorModal";
import { ReviewFlightItinerary } from "./components/ReviewFlightItinerary";
import { ReviewHotelItinerary } from "./components/ReviewHotelItinerary";
import * as constants from "./constants";
import { useCorePackagesSync } from "../../hooks/useCorePackagesSync";
import { CorePackagesNavigationProvider } from "../../context/CorePackagesNavigationContext";
import { PackageItineraryReviewConnectorProps } from "./container";
import "./styles.scss";
import {
  PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
  PACKAGES_LIST_OPTIMIZATIONS_V1,
  useExperiments,
  useIsPackagesShoppingApiV2Enabled,
  XMOD_V1_EXPERIMENT,
} from "../../context/experiments";
import { PACKAGES_HOMEPAGE_HEADER_ACCOUNT_NAV_SLOT_ID } from "../../constants/homepageHeaderAccountNav";

interface IPackageItineraryReviewProps
  extends PackageItineraryReviewConnectorProps,
    RouteComponentProps {
  core?: CorePackagesCheckout;
  coreV2?: CorePackagesCheckoutV2;
}

export const PackageItineraryReview = ({
  finalizePackage,
  selectedPackageByLodging,
  rewardsKey,
  nightCount,
  travelersCount,
  history,
  location,
  departureDate,
  returnDate,
  finalizePackageCallState,
  selectedFareDetails,
  opaqueQuoteRequest,
  opaquePackageToken,
  searchAdultsCount,
  searchChildren,
  searchedInfants,
  core,
  coreV2,
  packagedAirComponentV2,
  packagedRoomComponentV2,
  packagePollState,
  packagePricing,
}: IPackageItineraryReviewProps) => {
  const synced = useCorePackagesSync(core, coreV2);

  const { matchesMobile } = useDeviceTypes();
  const [openChangeRoomModal, setOpenChangeRoomModal] = useState(false);
  const [openChangeHotelModal, setOpenChangeHotelModal] = useState(false);
  const [openChangeOutboundFlightModal, setOpenChangeOutboundFlightModal] =
    useState(false);
  const clientContext = useContext(ClientContext);
  const { logo, showCoreHomepage } = clientContext;
  const scrollTrigger = useScrollTrigger({ disableHysteresis: true });

  const { isTravelApp } = useIsTravelApp();

  const expState = useExperiments();

  const packagesListEnhancementsEnabled =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_LIST_OPTIMIZATIONS_V1,
    ) === AVAILABLE;

  const isPackageCheckoutOptimizationExperiment =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
    ) === AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const isPackagesShoppingApiV2Enabled = useIsPackagesShoppingApiV2Enabled();

  const isVesper = isVesperTenant(getConfigTenant());

  const packageFinalizeNotComplete = [
    CallState.NotCalled,
    CallState.InProcess,
  ].includes(finalizePackageCallState);

  const finalizeCallFailed = finalizePackageCallState === CallState.Failed;

  useEffect(() => {
    if (finalizeCallFailed) {
      trackEvent({
        eventName: REDIRECT_TO_PACKAGE_HOTEL_SHOP,
        properties: {
          screen: "package_itinerary_review",
          reason: "missing_package_finalize_response",
        },
        encryptedProperties: [],
      });
    }
  }, [finalizeCallFailed]);

  useEffect(() => {
    let versionPollTarget: number | undefined;
    if (isPackagesShoppingApiV2Enabled) {
      const { packageVersionPollTarget } = queryStringParser.parse(
        location.search
      );
      const parsed = Number(packageVersionPollTarget);
      versionPollTarget = Number.isInteger(parsed) ? parsed : undefined;
    }

    finalizePackage(versionPollTarget, isPackagesShoppingApiV2Enabled);
    trackEvent({
      eventName: VIEWED_ITINERARY_REVIEW,
      properties: {},
      encryptedProperties: [],
    });
  }, []);

  useEffect(() => {
    // Wait for the b2bportal handoff to seed the store before deciding the data is
    // missing. Until sync completes, selectedPackageByLodging is null only because the
    // store hasn't been hydrated yet — not because the handoff failed.
    if ((core != null || coreV2 != null) && !synced) return;

    // Under v2, the selection lives in the GetPackage snapshot, populated
    // asynchronously by pollPackageVersion (triggered from the mount effect
    // above). Don't treat "not populated yet" as "missing" — wait for the poll
    // to settle (succeed, exhaust its retries, or determine no poll was
    // needed) before deciding the selection is genuinely absent. Without this,
    // this effect's first pass (packagePollState still "idle"/"polling") would
    // redirect before the async fetch ever had a chance to resolve. Also
    // requires the flight product, not just the room — matches v0's existing
    // guarantee that a package always has a flight selection.
    const isMissingSelection = isPackagesShoppingApiV2Enabled
      ? packagePollState === "settled" &&
        (packagedRoomComponentV2 == null || packagedAirComponentV2 == null)
      : !selectedPackageByLodging || !selectedFareDetails;

    if (isMissingSelection) {
      trackEvent({
        eventName: REDIRECT_TO_PACKAGE_HOTEL_SHOP,
        properties: {
          screen: "package_itinerary_review",
          reason: (() => {
            switch (true) {
              case !selectedPackageByLodging &&
                !opaqueQuoteRequest &&
                !opaquePackageToken:
                return "missing_hotel_and_flight_tokens";
              case !selectedPackageByLodging && !opaqueQuoteRequest:
                return "missing_hotel_token";
              case !selectedPackageByLodging && !opaquePackageToken:
                return "missing_flight_token";
              case !selectedPackageByLodging:
                return "missing_selected_package";
              default:
                return "missing_selected_fare_details";
            }
          })(),
        },
        encryptedProperties: [],
      });

      if (core?.goToHotelShop != null) {
        core.goToHotelShop();
      } else if (coreV2?.goToHotelShop != null) {
        coreV2.goToHotelShop();
      } else {
        goToHotelShop({ history });
      }
    }
  }, [
    selectedPackageByLodging,
    selectedFareDetails,
    packagedRoomComponentV2,
    packagedAirComponentV2,
    packagePollState,
    synced,
    core,
    coreV2,
  ]);

  const childrenAges = useMemo(() => {
    const infantsAges = searchedInfants.map((infant) => infant.age);
    return [...searchChildren, ...infantsAges];
  }, [searchedInfants, searchChildren]);

  if (isPackagesShoppingApiV2Enabled) {
    // Still waiting on pollPackageVersion to settle — render a loading state
    // instead of redirecting prematurely (the gate effect above won't decide
    // anything is "missing" until packagePollState reaches "settled" either).
    if (packagedRoomComponentV2 == null) {
      return packagePollState === "settled" ? null : (
        <Skeleton className="package-itinerary-review-loading-skeleton" />
      );
    }
  } else if (!selectedPackageByLodging || !selectedFareDetails) {
    return null;
  }

  // Both branches above return before this point unless the selection (and so
  // packagePricing, resolved by the same container-level accessor) is present.
  const resolvedPackagePricing = packagePricing as PackagePricing;

  if ((core != null || coreV2 != null) && !synced) return null;

  return (
    <CorePackagesNavigationProvider core={core} coreV2={coreV2}>
      {finalizeCallFailed ? (
        <ReviewErrorModal
          returnToShop={() => {
            if (core?.goToHotelShop != null) {
              core.goToHotelShop();
            } else {
              goToHotelShop({ history });
            }
          }}
        />
      ) : (
        <Box
          className={clsx("package-itinerary-review-root", {
            mobile: matchesMobile,
            "travel-app": isTravelApp,
            xmod: isXmodRedesign,
            redesigned: isVesper,
          })}
        >
          {matchesMobile ? (
            <Box
              className={clsx("mobile-review-pkg-header-container", {
                scrolled: scrollTrigger,
                "travel-app": isTravelApp,
              })}
            >
              <Box className="go-back-button-section">
                {isTravelApp ? undefined : (
                  <ActionLink
                    className={clsx("go-back-button")}
                    onClick={() => {
                      history.goBack();
                    }}
                    content={<FontAwesomeIcon icon={faChevronLeft} />}
                  />
                )}
              </Box>
              <Box
                className={clsx(
                  "locations-and-dates-section",
                  "header-middle-section",
                )}
              >
                <>
                  <Typography variant="body1" className="locations-section">
                    {constants.REVIEW_PACKAGE_DETAILS}
                  </Typography>
                  <Box className="dates-section">
                    {departureDate && returnDate && (
                      <Typography variant="body2">
                        {dayjs(departureDate).format("ddd, MMM DD")} -{" "}
                        {dayjs(returnDate).format("ddd, MMM DD")}
                      </Typography>
                    )}
                  </Box>
                </>
              </Box>
            </Box>
          ) : (
            <Header
              className="rewards-components-section"
              left={
                <Box className="rewards-account-section-left-content">
                  <Box className="logo" onClick={() => history.push(PATH_HOME)}>
                    {logo}
                  </Box>

                  <Box className="rewards-account-section-travel-details">
                    <Typography variant="body1" tabIndex={0}>
                      {constants.REVIEW_HOTEL_AND_FLIGHT}
                    </Typography>
                    {departureDate && returnDate && (
                      <Typography variant="body2">
                        {dayjs(departureDate).format("ddd, MMM DD")} -{" "}
                        {dayjs(returnDate).format("ddd, MMM DD")}
                      </Typography>
                    )}
                  </Box>
                </Box>
              }
              right={
                showCoreHomepage ? (
                  <Box
                    className="package-itinerary-review-homepage-account-nav"
                    id={PACKAGES_HOMEPAGE_HEADER_ACCOUNT_NAV_SLOT_ID}
                  />
                ) : (
                  <Box className="desktop-flight-list-rewards-account-contents">
                    <RewardsAccountSelection
                      className={clsx("b2b hide-balance-border")}
                      popoverClassName="b2b"
                    />
                    <TravelWalletDrawer />
                  </Box>
                )
              }
            />
          )}
          <PackagesShopProgressBar isMobile={matchesMobile} />
          {matchesMobile && (
            <>
              <Box className="mobile-rewards-account-contents">
                <RewardsAccountSelection
                  className="b2b"
                  popoverClassName="b2b"
                />
              </Box>
              <Box className="pricing-details-wrapper">
                <PackageHotelPriceAndRewardsDisplay
                  pricing={resolvedPackagePricing}
                  nightCount={nightCount ?? null}
                  travelersCount={travelersCount}
                  rewardsKey={rewardsKey}
                  showEnhancedUI={packagesListEnhancementsEnabled}
                />
              </Box>
              <PackageDiscountBanner
                discount={resolvedPackagePricing.totalPackageSavings.fiat}
                isPackageCheckoutOptimizationExperiment={
                  isPackageCheckoutOptimizationExperiment
                }
              />
              <Divider />
            </>
          )}
          <Box className="hotel-itinerary-header">
            <Icon name={IconName.HotelFunnelIcon} />
            <Typography className="header-text" variant="h3">
              {constants.REVIEW_HOTEL}
            </Typography>
          </Box>
          <Box className={clsx("package-itinerary-review-wrapper")}>
            <Box className="left-section">
              <ReviewHotelItinerary
                setOpenChangeHotelModal={setOpenChangeHotelModal}
                setOpenChangeRoomModal={setOpenChangeRoomModal}
                showCardContentIcons={!matchesMobile}
                adultsCount={searchAdultsCount}
                childrenAges={childrenAges}
              />
              <Divider />
              <Box className="flight-itinerary-header">
                <Icon name={IconName.FlightFunnelIcon} />
                <Typography className="header-text" variant="h3">
                  {constants.REVIEW_FLIGHT}
                </Typography>
              </Box>
              <ReviewFlightItinerary
                setOpenChangeOutboundFlightModal={
                  setOpenChangeOutboundFlightModal
                }
                showChangeCTAs
              />

              <Divider className="ancillary-disclaimer-divider" />

              <Typography className="ancillary-disclaimer-text">
                {ANCILLARY_DISCLAIMER_TEXT}
              </Typography>
            </Box>
            {matchesMobile ? (
              <Box className="continue-button-section">
                {packageFinalizeNotComplete ? (
                  <Skeleton className="pkg-review-continue-cta-loading-skeleton" />
                ) : (
                  <ActionButton
                    className="continue-button b2b"
                    onClick={() => goToBook({ history })}
                    message={constants.CONTINUE_TO_CHECKOUT}
                  />
                )}
              </Box>
            ) : (
              <Box className="right-section">
                <Box className="right-section-sticky-content">
                  <Box className="review-itinerary-card-content-container pricing-details">
                    <PackageHotelPriceAndRewardsDisplay
                      pricing={resolvedPackagePricing}
                      nightCount={nightCount ?? null}
                      travelersCount={travelersCount}
                      rewardsKey={rewardsKey}
                      showEnhancedUI={packagesListEnhancementsEnabled}
                    />
                    {packageFinalizeNotComplete ? (
                      <Skeleton className="pkg-review-continue-cta-loading-skeleton" />
                    ) : (
                      <Button
                        className="continue-button"
                        onClick={() => goToBook({ history })}
                      >
                        {constants.CONTINUE_TO_CHECKOUT}
                      </Button>
                    )}
                  </Box>

                  <PackageDiscountBanner
                    discount={resolvedPackagePricing.totalPackageSavings.fiat}
                    isPackageCheckoutOptimizationExperiment={
                      isPackageCheckoutOptimizationExperiment
                    }
                  />
                </Box>
              </Box>
            )}
          </Box>

          {(openChangeHotelModal ||
            openChangeRoomModal ||
            openChangeOutboundFlightModal) && (
            <ChangeItineraryModal
              type={
                openChangeHotelModal
                  ? "hotel"
                  : openChangeRoomModal
                  ? "room"
                  : "outbound-flight"
              }
              openModal={
                openChangeHotelModal ||
                openChangeRoomModal ||
                openChangeOutboundFlightModal
              }
              setOpenModal={
                openChangeHotelModal
                  ? setOpenChangeHotelModal
                  : openChangeRoomModal
                  ? setOpenChangeRoomModal
                  : setOpenChangeOutboundFlightModal
              }
              isMobile={matchesMobile}
            />
          )}
        </Box>
      )}
    </CorePackagesNavigationProvider>
  );
};
